// Ubicación en el repo: src/lib/branchSchedule.js
// Horario semanal + almuerzo por sucursal (migración 0043, 23-ago-2026, ver
// DECISIONS.md) — pedido de Nicolás: el auto-agendamiento público
// ofrecía 9:00-17:45 fijo, igual para las 7 sucursales y los 7 días,
// sin pausa de almuerzo. Ahora es configurable desde BranchFormModal.
//
// `branches.schedule` (jsonb) guarda 7 llaves fijas lunes→domingo, cada una
// `{ open: 'HH:MM', close: 'HH:MM', lunchStart: 'HH:MM'|null, lunchEnd:
// 'HH:MM'|null }` o `null` si ese día la sucursal está cerrada. Columna
// entera en `null` = "todavía no configurado" — la Edge Function de
// disponibilidad (public-agendamiento-availability) cae al horario fijo de
// siempre en ese caso, retrocompatible mientras Nicolás no cargue nada acá.
//
// Este módulo traduce entre esa forma "de base de datos" (compacta, la que
// entiende también la Edge Function en Deno — duplicada ahí, mismo patrón
// que TYPE_CONFIG/ORL_ROLES) y una forma "de formulario" (más cómoda para
// inputs de React: un booleano `enabled` en vez de inferirlo de si el día
// es `null`, y un booleano `lunch` separado en vez de inferirlo de si
// lunchStart/lunchEnd existen).

export const SCHEDULE_DAYS = [
  { key: 'mon', label: 'Lunes' },
  { key: 'tue', label: 'Martes' },
  { key: 'wed', label: 'Miércoles' },
  { key: 'thu', label: 'Jueves' },
  { key: 'fri', label: 'Viernes' },
  { key: 'sat', label: 'Sábado' },
  { key: 'sun', label: 'Domingo' },
];

function emptyDayForm() {
  return { enabled: false, start: '', end: '', lunch: false, lunchStart: '', lunchEnd: '' };
}

// Plantilla inicial razonable para una sucursal sin horario cargado
// todavía (lunes a viernes 09:00-18:00 con almuerzo 13:00-14:00, sábado
// 09:00-14:00 sin almuerzo, domingo cerrado) — punto de partida editable,
// Nicolás lo ajusta y recién ahí se guarda (no se guarda solo por mostrarse).
export function defaultScheduleForm() {
  const weekday = { enabled: true, start: '09:00', end: '18:00', lunch: true, lunchStart: '13:00', lunchEnd: '14:00' };
  const saturday = { enabled: true, start: '09:00', end: '14:00', lunch: false, lunchStart: '', lunchEnd: '' };
  const form = {};
  for (const { key } of SCHEDULE_DAYS) {
    form[key] = key === 'sun' ? emptyDayForm() : key === 'sat' ? { ...saturday } : { ...weekday };
  }
  return form;
}

// jsonb de la base de datos -> estado de formulario. `schedule` puede ser
// `null` (sucursal nueva o todavía sin configurar).
export function scheduleToForm(schedule) {
  if (!schedule) return defaultScheduleForm();
  const form = {};
  for (const { key } of SCHEDULE_DAYS) {
    const d = schedule[key];
    form[key] = d
      ? {
          enabled: true,
          start: d.open || '',
          end: d.close || '',
          lunch: !!(d.lunchStart && d.lunchEnd),
          lunchStart: d.lunchStart || '',
          lunchEnd: d.lunchEnd || '',
        }
      : emptyDayForm();
  }
  return form;
}

// Estado de formulario -> jsonb para guardar. Un día sin `enabled`, o sin
// hora de inicio/fin cargada, se guarda como `null` (cerrado) — no tiene
// sentido guardar un día "abierto" sin horas.
export function formToSchedule(form) {
  const out = {};
  for (const { key } of SCHEDULE_DAYS) {
    const d = form[key];
    if (!d?.enabled || !d.start || !d.end) {
      out[key] = null;
      continue;
    }
    out[key] = {
      open: d.start,
      close: d.end,
      lunchStart: d.lunch && d.lunchStart ? d.lunchStart : null,
      lunchEnd: d.lunch && d.lunchEnd ? d.lunchEnd : null,
    };
  }
  return out;
}
